<script>
    import {onMount} from 'svelte';

    import {slide} from 'svelte/transition';
    import {cubicInOut} from 'svelte/easing';

    import classnames from 'classnames';

    import {userStore} from '@js/stores/userStore';
    import {mapStore} from '@js/stores/mapStore';

    import * as Utils from '@js/modules/utils';

    import {ElevationRideTypes, RideLevels, RouteTypeToRideType} from '@js/components/modules/constants';

    import {MAP_CLASS, MAP_MODULES} from '@js/modules/maps/mapConstants';
    import MapBuilder from '@js/modules/maps/MapBuilder';
    import MapToolbar from '@js/components/maps/MapToolbar.svelte';
    import MapLayers from '@js/components/maps/MapLayers.svelte';
    import MapLoader from '@js/components/maps/MapLoader.svelte';
    import MapDataOutline from '@js/components/maps/MapDataOutline.svelte';
    import MapHelper from '@js/components/maps/MapHelper.svelte';
    import MapElevationButton from '@js/components/maps/MapElevationButton.svelte';
    import MapElevationGraph from '@js/components/maps/MapElevationGraph.svelte';
    import MapElevationComputation from '@js/components/maps/MapElevationComputation.svelte';

    import StorageService from '@js/modules/storage';
    import AnalyticsService from '@js/modules/analyticsService';
    import Computation from '@js/modules/computation';

    const {
        mapId,
        class: className,
        rideId,
        reusePreviousTracks = true,
        initialMarkerPosition,
        initialMapPoints,
        onDistanceChange
    } = $props();

    const TIME_STORAGE_KEY = 'MapTimeStore';

    let _previousMapDistance = 0;
    let _previousMapLocationValue;
    let _previousRideType;
    let _previousIsochroneType;
    let _previousIsochroneValue;
    let _previousUnit = userStore.unit;

    let isShowElevation = $state(false);
    let elevationData = $state();

    const _initialMapPoints = () => {
        if (initialMapPoints) {
            if (Utils.is()
                .isString(initialMapPoints)) {
                const stringPoints = initialMapPoints.split(';');
                return stringPoints.map((stringPoint) => {
                    const point = stringPoint.split(',');

                    if (point.length > 2) {
                        return {
                            type: point[0],
                            latitude: parseFloat(point[1]),
                            longitude: parseFloat(point[2]),
                            delta_distance: point[4] && parseFloat(point[3]),
                            delta_time: point[4] && parseFloat(point[4]),
                            delta_pause: point[5] && parseFloat(point[5])
                        };
                    } else {
                        return null;
                    }
                })
                    .compact();
            } else {
                return initialMapPoints;
            }
        } else {
            return null;
        }
    };

    const _computeElevation = () => {
        if (!mapStore.map || !mapStore.currentDistance) {
            return;
        }

        mapStore.map.computeElevation((coordinateElevations, simplifiedCoordinateElevations) => {
            mapStore.durationComputed = true;
            mapStore.currentDuration = Computation.estimateDuration(mapStore.currentRideType, mapStore.currentDistance, coordinateElevations.map((coordEl) => [coordEl.deltaDistance, coordEl.elevation]));

            mapStore.speedComputed = true;
            mapStore.currentSpeed = Computation.computeSpeed(mapStore.currentDistance, mapStore.currentDuration, {formatted: false});

            mapStore.currentLevel = RideLevels[Computation.estimateLevel(mapStore.currentRideType, mapStore.currentDistance, coordinateElevations.map((coordEl) => [coordEl.deltaDistance, coordEl.elevation]))];

            if (ElevationRideTypes.includes(mapStore.currentRideType)) {
                mapStore.elevationApsis = Computation.computeElevationApsis(simplifiedCoordinateElevations.map((c) => c[1]));
            }

            mapStore.map.updatePointsWithElevation(coordinateElevations);

            if (coordinateElevations.length) {
                elevationData = {
                    positiveElevation: mapStore.elevationApsis.positiveElevation,
                    negativeElevation: mapStore.elevationApsis.negativeElevation,
                    minimumAltitude: mapStore.elevationApsis.minimumAltitude,
                    maximumAltitude: mapStore.elevationApsis.maximumAltitude,
                    charts: {
                        elevation: true,
                        speed: false,
                        pace: false
                    },
                    elevation: {
                        xUnits: 'km',
                        yUnits: 'm'
                    },
                    points: coordinateElevations.map((coordEl) => ({
                        distance: coordEl.totalDistance / 1000,
                        elevation: coordEl.elevation,
                        lat: coordEl.lat,
                        lng: coordEl.lng
                    }))
                };
            }
        });
    };

    const _handleMapDistanceChange = (newDistanceInKm) => {
        if (mapId === 'planner-map' && !_previousMapDistance && !!newDistanceInKm) {
            AnalyticsService.trackPlannerPathStarted();
        }

        const formattedDistance = Math.round(newDistanceInKm * 1000);

        if (mapStore.currentDistance === formattedDistance) {
            return;
        }

        if (newDistanceInKm === 0) {
            mapStore.durationComputed = true;
            mapStore.currentDuration = 0;
            mapStore.speedComputed = true;
            mapStore.currentSpeed = 0;
            mapStore.currentLevel = undefined;

            if (ElevationRideTypes.includes(mapStore.currentRideType)) {
                mapStore.elevationApsis = {
                    positiveElevation: null,
                    negativeElevation: null,
                    minimumAltitude: null,
                    maximumAltitude: null
                };
            }
        }

        if (onDistanceChange) {
            onDistanceChange(formattedDistance);
        } else {
            mapStore.currentDistance = formattedDistance;

            if (mapStore.currentDistance) {
                if (mapStore.currentDistance && _previousMapDistance !== mapStore.currentDistance) {
                    const deltaDistance = Math.floor(Math.abs(mapStore.currentDistance - (_previousMapDistance || 0)));

                    if (deltaDistance > 0) {
                        AnalyticsService.trackMapDistanceComputed(deltaDistance);
                    }

                    _previousMapDistance = mapStore.currentDistance;
                }
            }
        }

        _computeElevation();
    };

    const _handleStartPointChange = (coordinates) => {
        mapStore.startPointLocation = coordinates;
    };

    const _handleUnitChange = (userState) => {
        if (userState.unit !== _previousUnit) {
            _previousUnit = userState.unit;

            mapStore.map.onUnitChanged(userState.unit);
        }
    };

    const _handleSnapToRoadChange = (checked) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.setSnapToRoad(checked);
    };

    const _handleRouteTypeChange = (routeType) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.currentRideType = RouteTypeToRideType[routeType];

        mapStore.map.setRouteType(routeType);
    };

    const _handleBackToHomeClick = () => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.backToHome();
    };

    const _handleUndoClick = () => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.undo();
    };

    const _handleClearClick = () => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.clearMap(() => {
            isShowElevation = false;
            elevationData = undefined;

            StorageService.removeItem(TIME_STORAGE_KEY);
        }, {
            noPopup: userStore.isConnected
        });
    };

    const _handleMapStyleChange = (mapStyle) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.setMapStyle(mapStyle);
    };

    const _handleElevationClick = () => {
        if (!mapStore.map) {
            return;
        }

        if (window.isMetricsEnabled && !isShowElevation) {
            AnalyticsService.trackComputeElevationClick('Planner page');
        }

        isShowElevation = !isShowElevation;
    };

    const _setMapLocation = (bbox, center, placeType) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.goToLocation(bbox, center, {
            showPositionMarker: true,
            // Use address our street or user geolocation as starting point
            startMarker: ['address', 'street', 'geolocation'].includes(placeType)
        });
    };

    const _setIsochrone = (type, value) => {
        if (!mapStore.map) {
            return;
        }

        if (mapStore.map.hasModule('ISOCHRONE')) {
            mapStore.map.setIsochrone(type, value);

            mapStore.map.redrawIsochrone();
        }
    };

    const _loadPlannerMap = () => {
        MapBuilder.createMap(
            MAP_CLASS.BUILD,
            [
                MAP_MODULES.TOOLBAR,
                MAP_MODULES.COMPUTE_ROAD,
                MAP_MODULES.DRAW_LINE,
                MAP_MODULES.RIDE_STORE,
                MAP_MODULES.RIDE_POIS,
                MAP_MODULES.ELEVATION_COMPUTATION,
                MAP_MODULES.GRAPH,
                MAP_MODULES.HELPER,
                MAP_MODULES.GEOLOCATION,
                MAP_MODULES.ISOCHRONE
            ],
            {
                mapId,
                rideId,
                reusePreviousTracks,
                initialPoints: _initialMapPoints(),
                initialLocation: initialMarkerPosition || mapStore.mapLocation,
                reusePosition: !initialMarkerPosition && !mapStore.mapLocation,
                useSearchMarker: !!initialMarkerPosition,
                hideMapScale: true,
                defaultZoom: 12
            }
        )
            .then((map) => {
                if (!map) {
                    return;
                }

                mapStore.map = map;

                mapStore.map.onDistanceUpdated(_handleMapDistanceChange);

                mapStore.map.onStartPointChanged(_handleStartPointChange);

                userStore.subscribe(_handleUnitChange);
            });
    };

    onMount(() => {
        if (!$mapStore.map) {
            _loadPlannerMap();
        }

        return () => {
            if (mapStore.map) {
                mapStore.map.destroy();
                mapStore.map = null;
            }
        };
    });

    $effect(() => {
        if ($mapStore.mapLocation && ($mapStore.mapLocation.bbox || $mapStore.mapLocation.center) && _previousMapLocationValue !== $mapStore.mapLocation.value) {
            _previousMapLocationValue = $mapStore.mapLocation.value;

            _setMapLocation($mapStore.mapLocation.bbox, $mapStore.mapLocation.center, $mapStore.mapLocation.placeType);
        }
    });

    $effect(() => {
        if ($mapStore.currentRideType && _previousRideType !== $mapStore.currentRideType) {
            _previousRideType = $mapStore.currentRideType;

            _computeElevation();
        }
    });

    $effect(() => {
        if ($mapStore.isochroneType && (_previousIsochroneType !== $mapStore.isochroneType || _previousIsochroneValue !== $mapStore.isochroneValue)) {
            _previousIsochroneType = $mapStore.isochroneType;
            _previousIsochroneValue = $mapStore.isochroneValue;

            _setIsochrone($mapStore.isochroneType, $mapStore.isochroneValue);
        }
    });
</script>

<div id={mapId}
     class="my-4">
    <div class="map-area relative overflow-visible">
        <div id={`${mapId}-map`}
             class={classnames('map-canvas overflow-hidden', className)}>
            <MapToolbar isSnapToRoadChecked={$userStore.featureFlags.map_snap_to_road_enabled}
                        useRouteType={false}
                        isRouteTypeAvailable={$userStore.featureFlags.map_route_type_enabled}
                        isPoiModeChecked={false}
                        onSnapToRoadChange={_handleSnapToRoadChange}
                        onRouteTypeChange={_handleRouteTypeChange}
                        onBackToHomeClick={_handleBackToHomeClick}
                        onUndoClick={_handleUndoClick}
                        onClearClick={_handleClearClick}/>

            {#if $userStore.featureFlags.map_available_layers?.length}
                <MapLayers mapLayersAvailable={$userStore.featureFlags.map_available_layers}
                           onMapStyleChange={_handleMapStyleChange}/>
            {/if}

            <MapHelper/>
        </div>

        <MapDataOutline/>

        <MapLoader/>
    </div>

    <MapElevationComputation/>

    {#if ElevationRideTypes.includes($mapStore.currentRideType)}
        {#if isShowElevation}
            <div transition:slide={{duration: 300, easing: cubicInOut}}>
                <MapElevationGraph map={$mapStore.map}
                                   currentRideType={$mapStore.currentRideType}
                                   elevationData={elevationData}
                                   warningHelper={true}/>
            </div>
        {/if}

        <MapElevationButton onElevationToggle={_handleElevationClick}/>
    {/if}
</div>
